import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import { useToggle } from 'react-use'
import type { Blog } from '@ztechapps/common.contract'
import { blogPermissions, getReservedBlogCategoryIds, isWhatsNewUnseen } from '@ztechapps/blog.common'
import { PublicationStatusEnum, WhatsNewAppSettingKeyEnum } from '@ztechapps/common.contract'
import { useDocument, useList, useRole, useUser, useUserSetting } from '@ztechapps/common.hook'
import { whatsNewPostQueries, whatsNewSeenQueries } from './graphql'

export type WhatsNewPost = Pick<Blog, '_id' | 'title' | 'slug' | 'contentHtml' | 'createdAt'>

export type UseWhatsNewProps = {
  /** True for an app whose blog categories carry no tenant, so the tenant-free What's New category is read. */
  tenantFree?: boolean
}

export type UseWhatsNewResult = {
  /** The newest Internal post of the What's New category; undefined while there is none. */
  post?: WhatsNewPost
  /** True when the user holds blog.canView. */
  canView: boolean
  /** True when the user has not yet seen the newest post. */
  unseen: boolean
  /** True until the signed-in user, blog.canView, the newest post, the stored seen id and the seen post have resolved once. */
  loading: boolean
  /** Stores the post's `_id` as the signed-in user's last seen What's New post. */
  markSeen: (postId: string) => Promise<void>
}

/**
 * The newest What's New post and whether the signed-in user has seen it. The seen row is the user's own setting
 * (`useUserSetting`). `tenantFree` reads the tenant-free category, for an app whose blog categories carry no tenant.
 * Without blog.canView no Blog is read.
 */
export const useWhatsNew = ({ tenantFree = false }: UseWhatsNewProps = {}): UseWhatsNewResult => {
  const { user } = useUser()
  const signedIn = !isEmpty(user?._id)
  const { hasRole: canView, loading: roleLoading } = useRole({ roleName: blogPermissions.canView.value })

  const categoryId = getReservedBlogCategoryIds(tenantFree ? undefined : user?.tenantId).whatsNew
  const { list, loading: postLoading } = useList<WhatsNewPost>({
    ...whatsNewPostQueries.allConfig,
    // Empty until signed in: `useList` keeps its filter in state and re-syncs it after the render, so a filter that
    // already holds the category id before the tenant is known would fire one query with the tenant-less id and
    // latch "no post" below. An absent key is filled from this prop on the render the user resolves.
    filter: signedIn ? { 'category._id': categoryId, status: PublicationStatusEnum.Internal } : {},
    sort: { createdAt: -1 },
    limit: 1,
    options: { queryOptions: { skip: !signedIn || !canView } },
  })
  const post = first(list)

  const {
    value: seenId,
    loading: settingLoading,
    save: markSeen,
  } = useUserSetting(WhatsNewAppSettingKeyEnum.WHATS_NEW_LAST_SEEN_BLOG)

  const { document: seen, loading: seenLoading } = useDocument<WhatsNewPost>({
    ...whatsNewSeenQueries.singleConfig,
    filter: { _id: seenId },
    options: { queryOptions: { skip: isEmpty(seenId) || !canView } },
  })

  // Latches on the first resolve and never resets: the refetch after a save reports `loading` again.
  const [resolved, setResolved] = useToggle(false)
  if (!resolved && signedIn && !roleLoading && !postLoading && !settingLoading && !seenLoading) setResolved(true)

  return {
    post,
    canView,
    unseen: isWhatsNewUnseen({ latest: post, seen }),
    loading: !resolved,
    markSeen,
  }
}
